/**
 * Encoge la foto ANTES de que salga del teléfono.
 *
 * Por qué existe: la cámara de un celular moderno entrega entre 3 y 6 MB por
 * foto. En una obra con una barra de señal eso son decenas de segundos por
 * pieza, y el operario está inventariando una pila de cuarenta. A 1600 px de
 * lado mayor y JPEG 0,82 la misma foto pesa ~250 KB —entre 10 y 20 veces
 * menos— y sigue siendo más resolución de la que cualquier pantalla de esta
 * app va a mostrar. Es la mejora más grande de F-3, y no toca infraestructura.
 *
 * ⚠️ `imageOrientation: 'from-image'` NO es un detalle estético. Una foto
 * vertical de iPhone llega con los píxeles acostados y la rotación declarada
 * aparte, en el EXIF. Al pasarla por un canvas el EXIF se descarta, así que sin
 * esta opción TODAS las fotos verticales quedan guardadas de costado — y ahí ya
 * no hay vuelta atrás, porque el original no se guarda. Con `from-image` el
 * navegador aplica la rotación al decodificar y el canvas recibe los píxeles
 * ya derechos.
 *
 * Qué NO hace: no genera miniatura. El plan original decía «1600 y 320», pero
 * hoy no existe la galería que mostraría la miniatura (eso es F-6), y una
 * miniatura sin consumidor es un blob de más por pieza y una columna de más que
 * mantener. Cuando llegue la galería se agrega acá, junto a su consumidor.
 */

/** Lado mayor de la foto que se guarda. Más que esto no lo muestra ninguna vista. */
const LADO_MAYOR = 1600;
/** 0,82 es el punto donde el JPEG deja de verse comprimido y todavía pesa poco. */
const CALIDAD = 0.82;

export interface FotoComprimida {
  archivo: Blob;
  /** Para poder decir en el chat/log cuánto se ahorró de verdad. */
  bytesOriginal: number;
  bytesFinal: number;
  ancho: number;
  alto: number;
}

/**
 * Devuelve la foto encogida, o la ORIGINAL intacta si algo falla.
 *
 * Nunca lanza: una pieza inventariada sin foto comprimida es un problema menor,
 * una pieza que no se pudo inventariar porque el canvas falló es un problema
 * caro. Ante la duda pasa el original y que el servidor decida.
 */
export async function comprimirFoto(original: File | Blob): Promise<FotoComprimida> {
  const fallback: FotoComprimida = {
    archivo: original,
    bytesOriginal: original.size,
    bytesFinal: original.size,
    ancho: 0,
    alto: 0,
  };

  try {
    const bitmap = await decodificar(original);
    const escala = Math.min(1, LADO_MAYOR / Math.max(bitmap.width, bitmap.height));
    const ancho = Math.round(bitmap.width * escala);
    const alto = Math.round(bitmap.height * escala);

    const lienzo = document.createElement('canvas');
    lienzo.width = ancho;
    lienzo.height = alto;
    const ctx = lienzo.getContext('2d');
    if (!ctx) return fallback;
    ctx.drawImage(bitmap, 0, 0, ancho, alto);
    if ('close' in bitmap) bitmap.close();

    const comprimida = await new Promise<Blob | null>(resolver =>
      lienzo.toBlob(resolver, 'image/jpeg', CALIDAD),
    );
    // Si comprimir dio MÁS grande (pasa con capturas chicas ya optimizadas),
    // quedarse con el original es lo correcto.
    if (!comprimida || comprimida.size >= original.size) return fallback;

    return {
      archivo: comprimida,
      bytesOriginal: original.size,
      bytesFinal: comprimida.size,
      ancho,
      alto,
    };
  } catch {
    return fallback;
  }
}

/**
 * `createImageBitmap` es el único camino que respeta el EXIF al decodificar.
 * El fallback por `<img>` existe para navegadores viejos: ahí la orientación
 * se pierde, pero una foto acostada es mejor que ninguna foto.
 */
async function decodificar(blob: Blob): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(blob, { imageOrientation: 'from-image' });
    } catch {
      // Safari viejo no soporta el diccionario de opciones: reintentar pelado.
      try {
        return await createImageBitmap(blob);
      } catch {
        /* cae al <img> */
      }
    }
  }

  const url = URL.createObjectURL(blob);
  try {
    return await new Promise<HTMLImageElement>((ok, falla) => {
      const img = new Image();
      img.onload = () => ok(img);
      img.onerror = () => falla(new Error('no se pudo decodificar la imagen'));
      img.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}
